import { EMPTY_PRODUCTS_LIST_FILTERS } from '@up2stay/up2promo-commons/products';
import { Button } from '@up2stay/up2promo-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { AlertCircle, Package, Plus, Search, X } from 'lucide-react';
import { shellPaths } from '@/core/router/app-paths';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

import { AppPageLayout } from '@/core/components/app-page-layout';
import {
  ListEmptyState,
  ListPageBody,
  ListSearchField,
  listToolbarFilterChipClass,
} from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import { ClaimRequestsStrip } from '@/core/components/products/claim-requests-panel';
import {
  ProductCardList,
  ProductCardListSkeleton,
} from '@/core/components/products/product-card-list';
import { ProductsListFilters } from '@/core/components/products/products-list-filters';
import { ProductsListSortSelect } from '@/core/components/products/products-list-sort-select';
import { ProductsLiveScanBanner } from '@/core/components/products/products-live-scan-banner';
import type { UseProductsPageReturn } from '@/core/components/products/use-products-page';
import {
  PRODUCTS_PAGE_SIZE_OPTIONS,
  ProductsCompactMetrics,
} from '@/routes/app/products';

export function ProductsPageContent({ page }: { page: UseProductsPageReturn }) {
  const isNative = isCapacitorNative();
  const {
    dict,
    navigateShell,
    canManageProducts,
    searchQuery,
    setSearchQuery,
    page: listPage,
    setPage,
    pageSize,
    setPageSize,
    productFilters,
    listSort,
    setListSort,
    liveScanEnabled,
    setLiveScanEnabled,
    highlightedProductId,
    productsDict,
    handleFiltersChange,
    locationIdToName,
    userIdToMember,
    locationFilterOptions,
    facetsData,
    productsLoading,
    productsFetching,
    productsError,
    refetchProducts,
    metricsData,
    metricsLoading,
    products,
    totalPages,
    totalCount,
    showMetrics,
    sortedProducts,
    hasActiveFilters,
    hasActiveSearchOrFilters,
    filterLabels,
    sortLabels,
    activeFilterChips,
    showingLabel,
    handleProductClick,
    liveScanLabels,
    connectionState,
    claimRequests,
    claimsLive,
  } = page;

  const connectLabel =
    productsDict?.connectProduct ?? productsDict?.addProduct ?? '';

  const headerActions = canManageProducts ? (
    <Button
      size="lg"
      className="min-h-11 w-full sm:w-auto"
      onClick={() => navigateShell(shellPaths.moreProductsSetup)}
    >
      <Plus className="mr-2 h-4 w-4" />
      {connectLabel}
    </Button>
  ) : undefined;

  const errorCard = productsError ? (
    <ListEmptyState
      tone="error"
      icon={<AlertCircle className="h-6 w-6" aria-hidden />}
      title={productsDict?.loadError?.title ?? ''}
      description={productsDict?.loadError?.description}
      action={
        <Button variant="outline" onClick={() => void refetchProducts()}>
          {productsDict?.loadError?.retry}
        </Button>
      }
    />
  ) : null;

  const noLocationLabel =
    productsDict?.list?.noLocation ??
    productsDict?.list?.filters?.allLocations ??
    'No location';
  const noProfileLabel = productsDict?.list?.noProfile ?? 'No profile assigned';
  const noTargetLabel = productsDict?.list?.noTarget ?? 'No destination';

  return (
    <AppPageLayout className="min-w-0 overflow-x-hidden">
      <PageHeader
        title={productsDict?.title ?? ''}
        breadcrumb={
          <>
            {dict.nav.navDomainWork}
            <span className="mx-1.5 text-muted-foreground/70">/</span>
            {dict.nav.products}
          </>
        }
        // Cap More stack: declare hub back sync so chrome does not wait for
        // ionViewDidEnter (avoids back-button pop-in / header jump).
        backHref={isNative ? shellPaths.moreHub : undefined}
        actions={headerActions}
        actionsLayout="stack"
      />
      <ListPageBody>
        <ClaimRequestsStrip
          pending={claimRequests.pending}
          recent={claimRequests.recent}
          canManage={canManageProducts}
          live={claimsLive}
        />
        {showMetrics ? (
          <ProductsCompactMetrics
            dict={productsDict}
            totalProducts={metricsData?.totalProducts ?? 0}
            totalScans30d={metricsData?.totalScans30d ?? 0}
            isLoading={metricsLoading}
          />
        ) : null}

        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <ListSearchField
              className="min-w-0 w-full sm:flex-1"
              type="search"
              variant="toolbar"
              placeholder={productsDict?.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={productsDict?.search}
            />
            <div className="flex min-w-0 w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
              <div className="flex min-w-0 w-full items-center gap-2 sm:w-auto">
                <ProductsListFilters
                  value={productFilters}
                  onChange={handleFiltersChange}
                  facets={facetsData}
                  locationOptions={locationFilterOptions}
                  labels={filterLabels}
                />
                <ProductsListSortSelect
                  value={listSort}
                  onChange={setListSort}
                  labels={sortLabels}
                />
              </div>
              <ProductsLiveScanBanner
                className="w-full justify-between sm:w-auto sm:justify-start"
                enabled={liveScanEnabled}
                onEnabledChange={setLiveScanEnabled}
                connectionState={connectionState}
                labels={liveScanLabels}
              />
            </div>
          </div>

          {hasActiveFilters ? (
            <div className="flex flex-wrap items-center gap-2">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className={`${listToolbarFilterChipClass} gap-1.5 border-border/60 bg-muted/40 text-foreground hover:bg-muted`}
                  onClick={chip.onRemove}
                >
                  <span className="truncate">{chip.label}</span>
                  <X className="size-3.5 shrink-0 opacity-70" aria-hidden />
                  <span className="sr-only">
                    {productsDict?.list?.filters?.clearFilters ??
                      'Clear filter'}
                  </span>
                </button>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-muted-foreground hover:text-foreground"
                onClick={() => handleFiltersChange(EMPTY_PRODUCTS_LIST_FILTERS)}
              >
                {productsDict?.list?.filters?.clearFilters ?? 'Clear filters'}
              </Button>
            </div>
          ) : null}
        </div>

        {errorCard}

        {!productsError && productsLoading && products.length === 0 ? (
          <ProductCardListSkeleton count={6} />
        ) : !productsError &&
          products.length === 0 &&
          !hasActiveSearchOrFilters ? (
          <ListEmptyState
            icon={<Package className="h-6 w-6" aria-hidden />}
            title={productsDict?.emptyTitle ?? ''}
            description={productsDict?.empty ?? ''}
            action={
              canManageProducts ? (
                <Button
                  size="lg"
                  className="min-h-11 gap-2"
                  data-guidance-target="products-empty"
                  onClick={() => navigateShell(shellPaths.moreProductsSetup)}
                >
                  <Plus className="size-4" />
                  {connectLabel}
                </Button>
              ) : undefined
            }
          />
        ) : !productsError &&
          products.length === 0 &&
          hasActiveSearchOrFilters ? (
          <ListEmptyState
            icon={<Search className="h-6 w-6" aria-hidden />}
            title={
              productsDict?.filterNoResults ??
              productsDict?.searchNoResults ??
              ''
            }
            action={
              hasActiveFilters ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleFiltersChange(EMPTY_PRODUCTS_LIST_FILTERS)
                  }
                >
                  {productsDict?.list?.filters?.clearFilters ?? 'Clear filters'}
                </Button>
              ) : undefined
            }
          />
        ) : !productsError ? (
          <>
            <ProductCardList
              products={sortedProducts}
              dict={productsDict}
              locationIdToName={locationIdToName}
              userIdToMember={userIdToMember}
              noLocationLabel={noLocationLabel}
              noProfileLabel={noProfileLabel}
              noTargetLabel={noTargetLabel}
              copyHashLabel={productsDict?.list?.copyHash}
              copyHashSuccess={productsDict?.list?.copyHashSuccess}
              highlightedProductId={highlightedProductId}
              isRefreshing={productsFetching && !productsLoading}
              onProductClick={handleProductClick}
            />
            {totalCount > 0 ? (
              <div className="flex flex-col items-center gap-3 pt-1 sm:flex-row sm:justify-between">
                <p className="text-center text-[13.5px] leading-relaxed text-muted-foreground sm:text-left">
                  {showingLabel}
                </p>
                {totalPages > 1 ? (
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Select
                      value={String(pageSize)}
                      onValueChange={(v) => {
                        setPageSize(Number(v));
                        setPage(1);
                      }}
                    >
                      <SelectTrigger
                        className="h-9 w-[8.5rem]"
                        aria-label={productsDict?.pagination?.pageSize}
                      >
                        <SelectValue
                          placeholder={productsDict?.pagination?.pageSize}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCTS_PAGE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={listPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      {productsDict?.pagination?.previous}
                    </Button>
                    <span className="text-sm text-muted-foreground tabular-nums">
                      {listPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={listPage >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      {productsDict?.pagination?.next}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </ListPageBody>
    </AppPageLayout>
  );
}
